import { ReactNode, useState } from "react";
import { LinkItem } from "~/components/organisms/navigation-bar/links";
import Image from "~/components/atoms/Image";
import { cn } from "~/lib/utils";
import CaretTriangle from "~/assets/caret-triangle-down.svg";

type Container = {
    children?: ReactNode;
};

function MenuText({ text }: { text: string }) {
    return <span className="text-[22px] font-bold text-blue-80">{text}</span>;
}
function SubMenuText({ text }: { text: string }) {
    return <span className="text-[17px] font-bold text-blue-80">{text}</span>;
}

function MenuItem({ isOpen, text }: { text: string; isOpen: boolean }) {
    return (
        <span className="flex flex-row items-center gap-2">
            <MenuText text={text} />
            <Image
                src={CaretTriangle}
                alt="dropdown-arrow"
                className={cn(
                    "z-0 mt-1 h-full w-[12px] transition-all",
                    isOpen ? "rotate-180" : "",
                )}
            />
        </span>
    );
}

export function CollapsibleMenu({ children, title }: Container & { title: string }) {
    const [open, setOpen] = useState<boolean>(false);
    return (
        <div className="cursor-pointer">
            <span onClick={() => setOpen(o => !o)}>
                <MenuItem text={title} isOpen={open} />
            </span>
            <div data-testid="collapsible-menu-content" className={cn(open ? "block" : "hidden")}>
                {children}
            </div>
        </div>
    );
}

export function SectionContainer({
    title,
    href,
    underlineOnHover,
    children,
}: Container & { title: string; href?: string; underlineOnHover?: boolean }) {
    const className = cn(
        "mb-3 text-[20px] font-bold text-blue-80",
        underlineOnHover ? "hover:underline" : "",
    );
    return (
        <div className="flex flex-col">
            {href ? (
                <a href={href} className={className}>
                    {title}
                </a>
            ) : (
                <span className="mb-1 text-[20px] font-bold text-blue-80">{title}</span>
            )}
            {children}
        </div>
    );
}
export function SubSectionContainer({ title, children }: Container & { title: string }) {
    return (
        <div>
            <div className="mb-[10px]">
                <SubMenuText text={title} />
            </div>
            {children}
        </div>
    );
}

export function LinkList({ links }: { links: LinkItem[] }) {
    return (
        <div className="flex flex-col">
            {links.map((link, index) => (
                <a
                    key={index}
                    className="py-1 font-normal text-blue-80 hover:underline"
                    href={link.href}
                    target={link.target}
                >
                    {link.label}
                </a>
            ))}
        </div>
    );
}

type Column = {
    title: string;
    links: LinkItem[];
};
export function DoubleColumnLinkList({
    primaryColumn,
    secondaryColumn,
}: {
    primaryColumn: Column;
    secondaryColumn: Column;
}) {
    return (
        <div className="grid grid-cols-2 gap-x-2">
            <div>
                <div className="font-bold text-blue-80">{primaryColumn.title}</div>
                <LinkList links={primaryColumn.links} />
            </div>
            <div>
                <div className="font-bold text-blue-80">{secondaryColumn.title}</div>
                <LinkList links={secondaryColumn.links} />
            </div>
        </div>
    );
}

export function GrayContainer({ children }: Container) {
    return <div className="rounded-xl bg-[#eff2f6] p-[15px]">{children}</div>;
}
